/**
 * Upload Queue Manager
 * Manages parallel uploads, resumability, and persistent tracking
 * Uses S3 presigned URLs for direct browser-to-S3 upload (bypasses server memory limits)
 */
import { getAccessToken } from '../lib/api';

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface UploadTask {
  id: string;
  file: File;
  requestId: string;
  requestType?: 'file-requests' | 'launch-requests';
  // Relative folder path (from webkitRelativePath) to preserve folder structure on request uploads
  folderPath?: string;
  status: 'pending' | 'uploading' | 'paused' | 'completed' | 'failed';
  progress: number;
  uploadedBytes: number;
  totalBytes: number;
  error?: string;
  startTime?: number;
  endTime?: number;
  comments?: string;
  // Set when the backend confirmed the upload but QC could not be initialised
  // (transient DB error, etc.). Editor sees this so they know the file is
  // held back from buyers until QC is re-attempted.
  qcWarning?: string;
  qcError?: string;
  // Creative metadata for UCODE generation
  creative_type?: string;
  creative_angle?: string;
  script_number?: string;
  // Real per-file video duration (seconds) + creative count — drive actual AI-cost tracking
  duration_seconds?: number;
  // Editor-entered cost per video (USD). This is the final LOCKED actual cost — stored verbatim
  // by the backend, NOT recomputed from the rate card.
  cost_usd?: number;
  num_creatives?: number;
  // Auto-retry tracking — bounded to prevent the retry storm we used to see
  attempts?: number;
  nextAttemptAt?: number;
}

const MAX_AUTO_RETRIES = 2;          // 1 initial + 2 retries = 3 total tries
const RETRY_BACKOFF_MS = [4000, 12000]; // 4s, then 12s

class UploadQueueManager {
  private queue: UploadTask[] = [];
  private activeUploads = 0;
  private maxConcurrent = 1; // Upload 1 file at a time to prevent server OOM with large files
  private listeners: ((queue: UploadTask[]) => void)[] = [];
  private abortControllers: Map<string, AbortController> = new Map();

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Add files to upload queue
   */
  addToQueue(files: File[], requestId: string, comments?: string, targetSubfolder?: string, metadata?: { creative_type?: string; creative_angle?: string; script_number?: string; durations?: (number | undefined)[]; costs?: (number | undefined)[] }, requestType?: 'file-requests' | 'launch-requests'): string[] {
    const taskIds: string[] = [];

    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      const file = files[fileIndex];
      const taskId = this.generateTaskId();

      // Preserve folder structure when user selected a directory
      // @ts-ignore
      const relativePath: string | undefined = (file as any).webkitRelativePath;
      let folderPath = relativePath
        ? relativePath.split('/').slice(0, -1).join('/')
        : undefined;

      // If a target subfolder is specified, prepend it to the folder path
      if (targetSubfolder) {
        folderPath = folderPath ? `${targetSubfolder}/${folderPath}` : targetSubfolder;
      }

      const task: UploadTask = {
        id: taskId,
        file,
        requestId,
        requestType: requestType || 'file-requests',
        folderPath,
        status: 'pending',
        progress: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
        comments,
        startTime: Date.now(),
        creative_type: metadata?.creative_type || undefined,
        creative_angle: metadata?.creative_angle || undefined,
        script_number: metadata?.script_number || undefined,
        duration_seconds: metadata?.durations?.[fileIndex],
        cost_usd: metadata?.costs?.[fileIndex],
        num_creatives: 1,
      };

      this.queue.push(task);
      taskIds.push(taskId);
    }

    this.saveToStorage();
    this.notifyListeners();
    this.processQueue();

    return taskIds;
  }

  /**
   * Process upload queue with parallel uploads
   */
  private async processQueue() {
    while (this.activeUploads < this.maxConcurrent) {
      const now = Date.now();
      const nextTask = this.queue.find(t => t.status === 'pending' && (!t.nextAttemptAt || t.nextAttemptAt <= now));
      if (!nextTask) break;

      this.activeUploads++;
      nextTask.status = 'uploading';
      this.notifyListeners();

      this.uploadTask(nextTask)
        .then(() => {
          nextTask.status = 'completed';
          nextTask.progress = 100;
          nextTask.endTime = Date.now();
          nextTask.attempts = (nextTask.attempts || 0) + 1;
        })
        .catch((error) => {
          nextTask.attempts = (nextTask.attempts || 0) + 1;
          nextTask.error = error.message;
          // Auto-retry with backoff if we haven't exhausted attempts.
          // 4xx errors are not retried — they're configuration issues
          // (bad token, file too large, etc.) and retrying just spams the server.
          const status = error?.response?.status;
          const isClientError = typeof status === 'number' && status >= 400 && status < 500;
          if (!isClientError && nextTask.attempts <= MAX_AUTO_RETRIES) {
            const delay = RETRY_BACKOFF_MS[nextTask.attempts - 1] || RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1];
            nextTask.status = 'pending';
            nextTask.nextAttemptAt = Date.now() + delay;
            setTimeout(() => {
              this.processQueue();
            }, delay);
          } else {
            nextTask.status = 'failed';
          }
        })
        .finally(() => {
          this.activeUploads--;
          nextTask.endTime = Date.now();
          this.saveToStorage();
          this.notifyListeners();
          this.processQueue(); // Process next in queue
        });
    }
  }

  /**
   * Upload a single task using S3 presigned URL (direct browser-to-S3).
   * Falls back to legacy server-side upload if presigned URL fails.
   */
  private async uploadTask(task: UploadTask): Promise<void> {
    const abortController = new AbortController();
    this.abortControllers.set(task.id, abortController);

    try {
      const token = getAccessToken();

      // Step 1: Get presigned upload URL from backend
      console.log(`📤 [Presigned] Getting upload URL for: ${task.file.name} (${task.file.size} bytes)`);
      let presignedData: { presignedUrl: string; s3Key: string; cloudfrontUrl: string } | null = null;
      const urlBase = task.requestType || 'file-requests';

      try {
        const urlResponse = await fetch(`${API_BASE_URL}/${urlBase}/${task.requestId}/get-upload-url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename: task.file.name,
            fileSize: task.file.size,
            mimeType: task.file.type || 'application/octet-stream',
            folderPath: task.folderPath || null,
            comments: task.comments || null,
            creative_type: task.creative_type || null,
            creative_angle: task.creative_angle || null,
            script_number: task.script_number || null,
            duration_seconds: task.duration_seconds ?? null,
            cost_usd: task.cost_usd ?? null,
            num_creatives: task.num_creatives ?? 1,
          }),
          signal: abortController.signal,
        });

        if (urlResponse.ok) {
          const data = await urlResponse.json();
          if (data.success && data.presignedUrl) {
            presignedData = data;
            console.log(`✅ [Presigned] Got URL, S3 key: ${data.s3Key}`);
          }
        } else if (urlResponse.status === 409) {
          // HI duplicate-name guard — do NOT fall back (the legacy path would
          // upload the whole file and fail identically). Surface the suggestion.
          const body = await urlResponse.json().catch(() => ({} as any));
          const err: any = new Error(body.error || 'A creative with this name already exists.');
          err.response = { status: 409, data: body };
          err.suggestedName = body.suggestedName;
          throw err;
        } else {
          console.warn(`⚠️ [Presigned] Server returned ${urlResponse.status}, falling back to legacy upload`);
        }
      } catch (e: any) {
        if (e.name === 'AbortError') throw e;
        if (e.response?.status === 409) throw e; // duplicate name — never fall back
        console.warn(`⚠️ [Presigned] Failed to get presigned URL, falling back to legacy upload:`, e.message);
      }

      if (presignedData) {
        // Step 2: Upload directly to S3 using presigned URL
        console.log(`🚀 [Presigned] Uploading directly to S3: ${task.file.name}`);

        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          let lastNotifiedProgress = 0;

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              task.uploadedBytes = e.loaded;
              const newProgress = Math.round((e.loaded / e.total) * 95);
              task.progress = newProgress;
              // Only notify on meaningful progress change to avoid flooding listeners
              if (newProgress - lastNotifiedProgress >= 3) {
                lastNotifiedProgress = newProgress;
                this.notifyListeners();
              }
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              console.log(`✅ [Presigned] S3 upload complete for: ${task.file.name}`);
              resolve();
            } else {
              console.error(`❌ [Presigned] S3 upload failed: ${xhr.status} ${xhr.statusText}`);
              reject(new Error(`S3 upload failed with status ${xhr.status}`));
            }
          });

          xhr.addEventListener('error', () => reject(new Error('Network error during S3 upload')));
          xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

          abortController.signal.addEventListener('abort', () => xhr.abort());

          xhr.open('PUT', presignedData!.presignedUrl);
          xhr.setRequestHeader('Content-Type', task.file.type || 'application/octet-stream');
          xhr.send(task.file);
        });

        // Step 3: Confirm upload with backend (create DB records)
        console.log(`📝 [Presigned] Confirming upload: ${task.file.name}`);
        task.progress = 97;
        this.notifyListeners();

        const confirmResponse = await fetch(`${API_BASE_URL}/${urlBase}/${task.requestId}/confirm-upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            s3Key: presignedData.s3Key,
            cloudfrontUrl: presignedData.cloudfrontUrl,
            filename: task.file.name,
            fileSize: task.file.size,
            mimeType: task.file.type || 'application/octet-stream',
            folderPath: task.folderPath || null,
            comments: task.comments || null,
            creative_type: task.creative_type || null,
            creative_angle: task.creative_angle || null,
            script_number: task.script_number || null,
            duration_seconds: task.duration_seconds ?? null,
            cost_usd: task.cost_usd ?? null,
            num_creatives: task.num_creatives ?? 1,
          }),
          signal: abortController.signal,
        });

        if (!confirmResponse.ok) {
          // Real upload failure — parse body for a user-friendly message.
          // QC issues are NOT routed through this path anymore (backend
          // returns 200 + qc_warning even if QC plumbing is stuck — the
          // file lands and the qcOrphanBackfill cron catches QC within 5
          // minutes). So a non-OK here means the upload itself failed
          // (network, auth, S3, etc.) — surface that clearly to the user.
          let userMsg = `Upload failed (${confirmResponse.status})`;
          try {
            const body = await confirmResponse.json();
            if (body && typeof body.error === 'string') userMsg = body.error;
          } catch (_) {
            try {
              const txt = await confirmResponse.text();
              if (txt) userMsg = txt.slice(0, 240);
            } catch (_) {}
          }
          const err: any = new Error(userMsg);
          err.response = { status: confirmResponse.status };
          throw err;
        }

        // Success path. The backend may still attach a qc_warning if QC
        // setup is being deferred to the backfill cron — show it as a
        // SOFT informational note, not an error. The file is uploaded
        // and safe; QC will catch up within 5 minutes.
        try {
          const confirmJson = await confirmResponse.json();
          if (confirmJson && confirmJson.qc_warning) {
            task.qcWarning = String(confirmJson.qc_warning);
            task.qcError = confirmJson.qc_error ? String(confirmJson.qc_error) : undefined;
            console.info(`ℹ️ [QC deferred] ${task.file.name}:`, task.qcWarning);
          }
        } catch (_) {
          // body wasn't JSON or already consumed — success still holds
        }

        task.progress = 100;
        task.uploadedBytes = task.totalBytes;
        console.log(`✅ [Presigned] Upload fully confirmed: ${task.file.name}`);
      } else {
        // FALLBACK: Legacy server-side upload (for backward compatibility)
        console.log(`📤 [Legacy] Uploading via server: ${task.file.name}`);
        await this.legacyUpload(task, abortController);
      }

      this.saveToStorage();
      this.notifyListeners();
    } finally {
      this.abortControllers.delete(task.id);
    }
  }

  /**
   * Legacy upload: send file through backend (FormData POST)
   * Used as fallback if presigned URL generation fails
   */
  private async legacyUpload(task: UploadTask, abortController: AbortController): Promise<void> {
    const formData = new FormData();
    formData.append('file', task.file);
    if (task.comments) formData.append('comments', task.comments);
    if (task.folderPath) formData.append('folder_path', task.folderPath);
    if (task.duration_seconds != null) formData.append('duration_seconds', String(task.duration_seconds));
    if (task.cost_usd != null) formData.append('cost_usd', String(task.cost_usd));
    formData.append('num_creatives', String(task.num_creatives ?? 1));

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          task.uploadedBytes = e.loaded;
          task.progress = Math.round((e.loaded / e.total) * 100);
          this.notifyListeners();
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          task.uploadedBytes = task.totalBytes;
          task.progress = 100;
          resolve();
        } else if (xhr.status === 409) {
          // HI duplicate-name guard — surface the suggested next counter.
          let msg = 'A creative with this name already exists.';
          try {
            const body = JSON.parse(xhr.responseText);
            if (body && body.error) msg = body.error;
          } catch (_) {}
          const err: any = new Error(msg);
          err.response = { status: 409 };
          reject(err);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

      abortController.signal.addEventListener('abort', () => xhr.abort());

      xhr.open('POST', `${API_BASE_URL}/${task.requestType || 'file-requests'}/${task.requestId}/upload`);
      const token = getAccessToken();
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  }

  /**
   * Pause an upload
   */
  pauseUpload(taskId: string) {
    const task = this.queue.find(t => t.id === taskId);
    if (task && task.status === 'uploading') {
      const controller = this.abortControllers.get(taskId);
      if (controller) {
        controller.abort();
      }
      task.status = 'paused';
      this.activeUploads--;
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Resume a paused upload
   */
  resumeUpload(taskId: string) {
    const task = this.queue.find(t => t.id === taskId);
    if (task && task.status === 'paused') {
      task.status = 'pending';
      this.saveToStorage();
      this.notifyListeners();
      this.processQueue();
    }
  }

  /**
   * Retry a failed upload
   */
  retryUpload(taskId: string) {
    const task = this.queue.find(t => t.id === taskId);
    if (task && task.status === 'failed') {
      task.status = 'pending';
      task.error = undefined;
      task.progress = 0;
      task.uploadedBytes = 0;
      task.attempts = 0;        // reset attempts so manual retry gets a fresh budget
      task.nextAttemptAt = undefined;
      this.saveToStorage();
      this.notifyListeners();
      this.processQueue();
    }
  }

  /**
   * Cancel an upload
   */
  cancelUpload(taskId: string) {
    const taskIndex = this.queue.findIndex(t => t.id === taskId);
    if (taskIndex !== -1) {
      const task = this.queue[taskIndex];

      if (task.status === 'uploading') {
        const controller = this.abortControllers.get(taskId);
        if (controller) {
          controller.abort();
        }
        this.activeUploads--;
      }

      this.queue.splice(taskIndex, 1);
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  /**
   * Clear completed uploads
   */
  clearCompleted() {
    this.queue = this.queue.filter(t => t.status !== 'completed');
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Get queue status
   */
  getQueue(): UploadTask[] {
    return this.queue;
  }

  /**
   * Subscribe to queue changes
   */
  subscribe(listener: (queue: UploadTask[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners
   */
  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.queue]));
  }

  /**
   * Save queue to localStorage
   */
  private saveToStorage() {
    try {
      const queueData = this.queue.map(task => ({
        ...task,
        file: undefined, // Don't store File object
        fileName: task.file?.name || 'Unknown',
        fileSize: task.file?.size || task.totalBytes,
        fileType: task.file?.type || 'unknown'
      }));
      localStorage.setItem('uploadQueue', JSON.stringify(queueData));
    } catch (error) {
      console.error('Failed to save upload queue:', error);
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadFromStorage() {
    try {
      const stored = localStorage.getItem('uploadQueue');
      if (stored) {
        const queueData = JSON.parse(stored);
        // Filter out completed uploads older than 24 hours
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        this.queue = queueData.filter((task: UploadTask) => {
          // Non-completed tasks from a prior session have no File object and can never finish.
          // Drop them so they don't block the single-concurrency slot on next page load.
          if (task.status !== 'completed') return false;
          if (task.endTime && task.endTime < oneDayAgo) return false;
          return true;
        });
        this.notifyListeners();
      }
    } catch (error) {
      console.error('Failed to load upload queue:', error);
    }
  }

  /**
   * Generate unique task ID
   */
  private generateTaskId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get upload statistics
   */
  getStats() {
    const total = this.queue.length;
    const completed = this.queue.filter(t => t.status === 'completed').length;
    const uploading = this.queue.filter(t => t.status === 'uploading').length;
    const failed = this.queue.filter(t => t.status === 'failed').length;
    const pending = this.queue.filter(t => t.status === 'pending').length;
    const paused = this.queue.filter(t => t.status === 'paused').length;

    return { total, completed, uploading, failed, pending, paused };
  }
}

// Singleton instance
export const uploadQueueManager = new UploadQueueManager();
